import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import {
  hasHarDecodedBodyView,
  type HarGrpcBodyView,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';
import {
  buildHarDisplayEditorProps,
  toasterApi,
} from '@terra/ui-design-system';
import { portalDecodeGrpcHarApi } from '../clients/tester-client/portal/portal-decode-grpc-har-api';
import type { ReplaySession } from '../store/replaySessionStore';
import { useReplaySessionStore } from '../store/replaySessionStore';
import { GRPC_WEB_SUPPORT_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import { useFeatureFlags } from './useFeatureFlags';
import type { BodyEncoding } from '../electron-shared/utils/binary-body';
import {
  hasGrpcHarHistoryEntry,
  isGrpcHarEntry,
  hasUsableRequestWebGrpcDecode,
  mergeWebGrpcDecodedData,
} from '../utils/harGrpcDisplay.utils';
import {
  parseEditedGrpcDecodedJsonBody,
  resolveEditorWireBody,
  transformReplayRequestBodyView,
} from '../utils/replayGrpcDisplay.utils';
import { buildReplayGrpcHarDisplayFromSession } from '../utils/replayOpenRequest.utils';
import { parseRequestFromText } from '../utils/httpFormatter';

function resolveInitialBodyView({
  requestData,
  originalUrl,
  storedWireBody,
  storedWireBodyEncoding,
}: {
  requestData: string;
  originalUrl: string;
  storedWireBody?: string;
  storedWireBodyEncoding?: BodyEncoding;
}): HarGrpcBodyView {
  const parsed = parseRequestFromText({ text: requestData, originalUrl });

  if (!parsed) {
    return 'decoded';
  }

  const body = parsed.body ?? '';

  if (parseEditedGrpcDecodedJsonBody(body)) {
    return 'decoded';
  }

  if (resolveEditorWireBody(body)) {
    return 'wire';
  }

  if (storedWireBodyEncoding === 'base64' && storedWireBody) {
    return 'wire';
  }

  return 'decoded';
}

function requestBodyMatchesView({
  requestData,
  originalUrl,
  bodyView,
  storedWireBody,
  storedWireBodyEncoding,
}: {
  requestData: string;
  originalUrl: string;
  bodyView: HarGrpcBodyView;
  storedWireBody?: string;
  storedWireBodyEncoding?: BodyEncoding;
}): boolean {
  const parsed = parseRequestFromText({ text: requestData, originalUrl });

  if (!parsed) {
    return true;
  }

  const body = parsed.body ?? '';

  if (bodyView === 'decoded') {
    return parseEditedGrpcDecodedJsonBody(body) !== null;
  }

  return (
    resolveEditorWireBody(body) !== null ||
    (storedWireBodyEncoding === 'base64' && Boolean(storedWireBody))
  );
}

export function useReplayGrpcBodyView({
  sessionId,
  session,
}: {
  sessionId: string;
  session: ReplaySession | undefined;
}) {
  const tGrpc = useTranslations('replay.grpc');
  const { isFeatureFlagEnabled: isGrpcWebSupportEnabled } = useFeatureFlags(
    GRPC_WEB_SUPPORT_ENABLED_FLAG,
  );
  const updateSessionGrpcReplayTransform = useReplaySessionStore(
    (state) => state.updateSessionGrpcReplayTransform,
  );
  const [bodyView, setBodyView] = useState<HarGrpcBodyView>('decoded');
  const [previewDecodedData, setPreviewDecodedData] =
    useState<WebGrpcDecodedData | null>(null);
  const [isTransformingBodyView, setIsTransformingBodyView] = useState(false);

  const currentResponse =
    session && session.currentResponseIndex >= 0
      ? (session.responseHistory[session.currentResponseIndex] ?? undefined)
      : undefined;

  const baseGrpcHarDisplay = useMemo((): StoredHarDisplayEntry | undefined => {
    if (!session) {
      return undefined;
    }

    const candidate =
      session.grpcHarDisplay ??
      buildReplayGrpcHarDisplayFromSession({
        session,
        currentResponse,
      });

    if (!candidate || !isGrpcHarEntry(candidate)) {
      return undefined;
    }

    return candidate;
  }, [session, currentResponse]);

  const bodyViewSessionKey = useMemo(() => {
    if (!session?.request.url) {
      return sessionId;
    }

    const grpcKey = session.grpcHarDisplay
      ? (session.grpcHarDisplay.webGrpcDecodedData?.schemaChecksum ?? 'grpc')
      : 'none';

    return [
      sessionId,
      session.request.url,
      session.request.body ?? '',
      session.request.bodyEncoding ?? '',
      grpcKey,
    ].join(':');
  }, [
    sessionId,
    session?.request.url,
    session?.request.body,
    session?.request.bodyEncoding,
    session?.grpcHarDisplay,
  ]);

  useEffect(() => {
    setPreviewDecodedData(null);
  }, [sessionId, baseGrpcHarDisplay]);

  useEffect(() => {
    const currentSession = useReplaySessionStore
      .getState()
      .sessions.find((entry) => entry.id === sessionId);

    if (!currentSession?.requestData) {
      setBodyView('decoded');

      return;
    }

    const initialView = resolveInitialBodyView({
      requestData: currentSession.requestData,
      originalUrl: currentSession.request.url,
      storedWireBody: currentSession.request.body,
      storedWireBodyEncoding: currentSession.request.bodyEncoding,
    });

    setBodyView(initialView);
  }, [bodyViewSessionKey, sessionId]);

  const grpcHarDisplay = useMemo((): StoredHarDisplayEntry | undefined => {
    if (!baseGrpcHarDisplay) {
      return undefined;
    }

    const mergedDecode = mergeWebGrpcDecodedData({
      base: baseGrpcHarDisplay.webGrpcDecodedData,
      overlay: previewDecodedData,
    });

    if (!mergedDecode) {
      return baseGrpcHarDisplay;
    }

    return {
      ...baseGrpcHarDisplay,
      webGrpcDecodedData: mergedDecode,
    };
  }, [baseGrpcHarDisplay, previewDecodedData]);

  const hasGrpcHarEntry = hasGrpcHarHistoryEntry({
    grpcHarDisplay: baseGrpcHarDisplay,
  });

  const hasGrpcBodyView = useMemo(
    () =>
      grpcHarDisplay
        ? hasHarDecodedBodyView({ harEntry: grpcHarDisplay })
        : false,
    [grpcHarDisplay],
  );

  useEffect(() => {
    const storedHasRequestDecode = hasUsableRequestWebGrpcDecode(
      baseGrpcHarDisplay?.webGrpcDecodedData,
    );

    if (
      !session?.applicationId ||
      !baseGrpcHarDisplay ||
      storedHasRequestDecode
    ) {
      return;
    }

    let cancelled = false;

    void portalDecodeGrpcHarApi
      .preview({
        applicationId: session.applicationId,
        request: baseGrpcHarDisplay.request,
        response: baseGrpcHarDisplay.response,
      })
      .then(({ webGrpcDecodedData }) => {
        if (cancelled || !webGrpcDecodedData) {
          return;
        }

        const mergedDecode = mergeWebGrpcDecodedData({
          base: baseGrpcHarDisplay.webGrpcDecodedData,
          overlay: webGrpcDecodedData,
        });

        if (!hasUsableRequestWebGrpcDecode(mergedDecode)) {
          return;
        }

        setPreviewDecodedData(webGrpcDecodedData);
      })
      .catch((error: unknown) => {
        console.warn('Failed to decode replay gRPC request preview', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    session?.applicationId,
    baseGrpcHarDisplay,
    sessionId,
    hasGrpcHarEntry,
    session?.request.url,
    currentResponse,
    session?.grpcHarDisplay,
  ]);

  const handleGrpcBodyViewChange = useCallback(
    async (view: HarGrpcBodyView) => {
      if (!session || !baseGrpcHarDisplay) {
        return;
      }

      if (
        view === bodyView &&
        requestBodyMatchesView({
          requestData: session.requestData,
          originalUrl: session.request.url,
          bodyView: view,
          storedWireBody: session.request.body,
          storedWireBodyEncoding: session.request.bodyEncoding,
        })
      ) {
        return;
      }

      if (!session.applicationId) {
        toasterApi.error({
          title: tGrpc('transformBodyErrorTitle'),
          description: tGrpc('missingSchemaDescription'),
        });

        return;
      }

      setIsTransformingBodyView(true);

      try {
        const transformed = await transformReplayRequestBodyView({
          applicationId: session.applicationId,
          grpcHarDisplay: baseGrpcHarDisplay,
          requestData: session.requestData,
          originalUrl: session.request.url,
          fromView: bodyView,
          toView: view,
          storedWireBody: session.request.body,
          storedWireBodyEncoding: session.request.bodyEncoding,
        });

        updateSessionGrpcReplayTransform({
          sessionId,
          requestData: transformed.requestData,
          wireBody: transformed.wireBody,
          wireBodyEncoding: transformed.wireBodyEncoding,
          grpcHarDisplay: transformed.grpcHarDisplay,
        });
        setBodyView(view);
        setPreviewDecodedData(null);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : tGrpc('transformBodyErrorFallbackDescription');

        toasterApi.error({
          title: tGrpc('transformBodyErrorTitle'),
          description: message,
        });
      } finally {
        setIsTransformingBodyView(false);
      }
    },
    [
      bodyView,
      session,
      baseGrpcHarDisplay,
      sessionId,
      updateSessionGrpcReplayTransform,
      tGrpc,
    ],
  );

  return {
    bodyView,
    grpcHarDisplay,
    hasGrpcHarEntry,
    hasGrpcBodyView,
    isTransformingBodyView,
    handleGrpcBodyViewChange,
    showProtocolBodyViewToggle: hasGrpcHarEntry && isGrpcWebSupportEnabled,
    showGrpcBodyViewToggle: hasGrpcHarEntry && isGrpcWebSupportEnabled,
    editorProps: buildHarDisplayEditorProps({
      showProtocolBodyViewToggle: hasGrpcHarEntry && isGrpcWebSupportEnabled,
      bodyView,
      onBodyViewChange: handleGrpcBodyViewChange,
      toggleDisabled: isTransformingBodyView,
    }),
  };
}
